import type { ReactNode } from "react";
import { useLocation } from "wouter";
import { ArrowLeft } from "lucide-react";

interface PageHeaderProps {
  /** Large title shown inline next to the back arrow. */
  title?: ReactNode;
  /** Optional supporting line shown directly under the title. */
  subtitle?: ReactNode;
  /** Where the back button navigates. Ignored if `onBack` is provided. */
  backHref?: string;
  /** Custom back handler (overrides `backHref`). Falls back to browser history. */
  onBack?: () => void;
  /** Accessible label for the back arrow. */
  backLabel?: string;
  /** Optional right-aligned slot on the header row (e.g. a Save button). */
  action?: ReactNode;
  /** Hide the back arrow (e.g. on a root tab page that has nowhere to go back to). */
  hideBack?: boolean;
  className?: string;
}

/**
 * Shared detail/edit page header: a ghost "← Back" link with a large title
 * below it (and an optional right-aligned action). Used by booking detail,
 * profile edit, event details… so they share one consistent treatment.
 */
export function PageHeader({
  title,
  subtitle,
  backHref,
  onBack,
  backLabel = "Back",
  action,
  hideBack = false,
  className = "",
}: PageHeaderProps) {
  const [, navigate] = useLocation();

  const handleBack = () => {
    if (onBack) return onBack();
    if (backHref) return navigate(backHref);
    window.history.back();
  };

  return (
    <div className={className}>
      {/* One row: back arrow + title on the left, optional action on the right. */}
      {(title != null || action != null || !hideBack) && (
        <div className="mb-6 mt-2">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2 min-w-0">
              {!hideBack && (
                <button
                  type="button"
                  onClick={handleBack}
                  aria-label={backLabel}
                  className="btn-ghost !px-2 inline-flex items-center justify-center shrink-0"
                >
                  <ArrowLeft className="w-6 h-6" />
                </button>
              )}
              {title != null && (
                <h1 className="text-3xl md:text-4xl font-bold truncate">{title}</h1>
              )}
            </div>
            {action}
          </div>
          {subtitle != null && (
            <p className="mt-2 text-sm leading-relaxed text-neutral-500 dark:text-neutral-400">
              {subtitle}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
